import { Drawer, Grid, Typography } from 'antd';
import { FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useRegulation } from '../../useRegulation';
import dayjs from 'dayjs';
import { orderBy } from 'lodash';
import classnames from 'classnames';
import { DATE_FORMAT } from '../../../../constants/app.ts';
import { MdOutlineArrowOutward } from 'react-icons/md';

const { useBreakpoint } = Grid;

const { Title, Text } = Typography;

const MoreInfo = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const screens = useBreakpoint();
  const { t } = useTranslation();
  const { data } = useRegulation();
  return (
    <>
      <Drawer
        title={t('common.more')}
        placement="left"
        closable={false}
        onClose={onClose}
        open={open}
        width={screens.xs ? '100%' : 365}
        extra={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <FiX fontSize={24} onClick={onClose} />
          </div>
        }
      >
        <Title style={{ padding: 0, margin: 0 }} level={4}>
          {t('common.specification')}
        </Title>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data?.regulationNumber && (
            <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="action-link-label">{t('common.number')}:</span>
              <span className="action-link-text">{data?.regulationNumber}</span>
            </Text>
          )}
          {data?.creationDate && (
            <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="action-link-label">{t('common.creation-date')}:</span>{' '}
              <span className="action-link-text">
                {dayjs(data?.creationDate).format('DD.MM.YYYY')}
              </span>
            </Text>
          )}
          {data?.modifiedAt && (
            <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="action-link-label">{t('common.edited')}:</span>{' '}
              <span className="action-link-text">
                {dayjs(data?.modifiedAt).format('DD.MM.YYYY')}
              </span>
            </Text>
          )}
          {data?.confirmationDate && (
            <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="action-link-label">{t('common.expiration')}:</span>{' '}
              <span className="action-link-text">
                {dayjs(data?.confirmationDate).format('DD.MM.YYYY')}
              </span>
            </Text>
          )}
          {data?.unitInCharge && (
            <Text
              type="secondary"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: 4,
              }}
            >
              <span className="action-link-text" style={{ textAlign: 'left' }}>
                {t('common.responsible')}:
              </span>
              <span className="action-link-text" style={{ textAlign: 'left' }}>
                {data?.unitInCharge?.title}
              </span>
            </Text>
          )}
          {data?.involvedUnits?.length > 0 && (
            <Text
              type="secondary"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: 4,
              }}
            >
              <span className="action-link-text" style={{ textAlign: 'left' }}>
                {t('common.in-collaboration')}:
              </span>
              <span className="action-link-text" style={{ textAlign: 'left' }}>
                {data?.involvedUnits?.map((unit: { title: string }) => unit.title).join(', ')}
              </span>
            </Text>
          )}
          {data?.emergedOut && (
            <Text
              type="secondary"
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: 4,
              }}
            >
              <span className="action-link-label" style={{ textAlign: 'left' }}>
                {t('form.field.emerged-out')}:
              </span>
              <span className="action-link-text" style={{ textAlign: 'left' }}>
                {data?.emergedOut}
              </span>
            </Text>
          )}
          {data?.publicationDate && !data?.isActive && (
            <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="action-link-label">{t('common.in-force')}:</span>{' '}
              <span className="action-link-text">
                {dayjs(data?.publicationDate).format('DD.MM.YYYY')}
              </span>
            </Text>
          )}
        </div>
        <Title style={{ padding: 0, margin: '20px 0 0 0' }} level={5}>
          {t('common.history')}
        </Title>
        {data?.versions.length > 0
          ? orderBy(data?.versions, 'publicationDate', 'desc')?.map(
              (version: { id: number; title: string; publicationDate: string }, index: number) => (
                <>
                  <Link
                    key={version.id}
                    to={
                      data?.versions.length === 1 ? '#' : `/regulation/${version.id}?history=true`
                    }
                  >
                    <div
                      className={classnames('draft-link', {
                        disabled: data?.versions.length === 1,
                      })}
                    >
                      {dayjs(version.publicationDate).format(DATE_FORMAT)}
                      <MdOutlineArrowOutward color="var(--primary-color)" />
                    </div>
                  </Link>
                  {!data?.isActive && index === data?.versions.length - 1 && (
                    <Link
                      key={version.id}
                      to={`/regulation/${orderBy(data?.versions, 'publicationDate', 'desc')[0].id}`}
                    >
                      <div className="draft-link">
                        {t('common.last-version')}
                        <MdOutlineArrowOutward color="var(--primary-color)" />
                      </div>
                    </Link>
                  )}
                </>
              ),
            )
          : null}
      </Drawer>
    </>
  );
};

export default MoreInfo;